import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import {Row, RowData} from "@tanstack/react-table";
import {ReactNode, RefObject, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import PrintPreviewScaler from "./PrintPreviewScaler";
import {PrintDocumentHeader, PrintPageSheet, PrintTable} from "./ServerTablePrintDocument";
import {PRINT_PAGE_SIZES} from "./constants";
import {
    ServerTablePrintColumn,
    ServerTablePrintOrientation,
    ServerTablePrintRenderMode
} from "./types";
import {getPrintContentWidth} from "./utils";
import {useServerTablePrintPagination} from "./useServerTablePrintPagination";

const PAGE_GAP = 16;

interface ServerTablePrintPreviewProps<TData extends RowData> {
    documentRef: RefObject<HTMLDivElement>
    title?: string
    subtitle: ReactNode
    orientation: ServerTablePrintOrientation
    fontSize: number
    columns: ServerTablePrintColumn<TData>[]
    rows: Row<TData>[]
    locale: string
    renderMode: ServerTablePrintRenderMode
    emptyText: string
    onPageCountChange?: (pageCount: number) => void
    onOversizedRowCountChange?: (count: number) => void
}

export function ServerTablePrintPreview<TData extends RowData>({
    documentRef,
    title,
    subtitle,
    orientation,
    fontSize,
    columns,
    rows,
    locale,
    renderMode,
    emptyText,
    onPageCountChange,
    onOversizedRowCountChange
}: ServerTablePrintPreviewProps<TData>) {
    const {t} = useTranslation();
    const size = PRINT_PAGE_SIZES[orientation];
    const measureRef = useRef<HTMLDivElement>(null);
    const {pages, oversizedRowCount} = useServerTablePrintPagination({
        measureRef,
        orientation,
        layoutDependencies: [rows, columns, fontSize, renderMode, title, subtitle]
    });

    useEffect(() => {
        if (pages) onPageCountChange?.(pages.length);
    }, [pages, onPageCountChange]);

    useEffect(() => {
        onOversizedRowCountChange?.(oversizedRowCount);
    }, [onOversizedRowCountChange, oversizedRowCount]);

    return (
        <Paper
            variant="outlined"
            sx={{
                p: {xs: 1, sm: 1.5},
                minHeight: {lg: '68vh'},
                maxHeight: {xs: '58vh', lg: '68vh'},
                overflow: 'auto',
                bgcolor: 'grey.100'
            }}
        >
            {/* Exact-width measurement keeps preview and printed pagination aligned. */}
            <Box
                ref={measureRef}
                aria-hidden
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: getPrintContentWidth(orientation),
                    visibility: 'hidden',
                    pointerEvents: 'none'
                }}
            >
                <PrintDocumentHeader title={title} subtitle={subtitle}/>
                <PrintTable
                    columns={columns}
                    rows={rows}
                    locale={locale}
                    fontSize={fontSize}
                    renderMode={renderMode}
                    emptyText={emptyText}
                />
            </Box>

            <PrintPreviewScaler pageWidth={size.width}>
                <Stack ref={documentRef} className="ServerTablePrintPages" sx={{gap: `${PAGE_GAP}px`}}>
                    {(pages ?? []).map((slice, pageIndex) => (
                        <PrintPageSheet
                            key={pageIndex}
                            orientation={orientation}
                            isLast={pageIndex === (pages?.length ?? 0) - 1}
                            pageLabel={(pages?.length ?? 0) > 1
                                ? t('Page {{page}} of {{total}}', {page: pageIndex + 1, total: pages?.length})
                                : undefined}
                        >
                            {pageIndex === 0 && (
                                <PrintDocumentHeader title={title} subtitle={subtitle}/>
                            )}
                            <PrintTable
                                columns={columns}
                                rows={rows.slice(slice.start, slice.end)}
                                locale={locale}
                                fontSize={fontSize}
                                renderMode={renderMode}
                                emptyText={emptyText}
                            />
                        </PrintPageSheet>
                    ))}
                </Stack>
            </PrintPreviewScaler>
        </Paper>
    );
}
